import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {Col, Container, Row} from 'reactstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCartPlus} from '@fortawesome/pro-light-svg-icons/faCartPlus';
import classNames from 'classnames';
import {DateTime} from 'luxon';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import {useUserState, useUserDispatch} from 'contexts/user';
import track from 'utils/track';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import Webinar from 'components/Webinar';
import CountdownTimer from 'components/CountdownTimer';
import Divider from 'components/Divider';
import SalesCopy from 'components/SalesCopy';
import {SecurityDisclaimer, TreesRow} from 'components/SalesCta';
import CheckoutButton from 'components/CheckoutButton';
import ModalExitScreening from 'components/ModalExitScreening';
import RegistrationModal from 'components/RegistrationModal';
import FollowButtons from 'components/FollowButtons';
import ShareButtons from 'components/ShareButtons';
import Footer from 'components/Footer';

//Due to a limitation of dotenv we must use three seperate statements
const {SF_API_URL} = process.env;
const {SITE_ID} = process.env;
const COMPLETED_SCREENING_EVENT = 'Watched TNTG Screening';

export default function Screening({screeningFull = false, ...props}) {
  const {
    phases: {
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();
  const phase = webinarSchedule.phase;
  const phaseDispatch = usePhaseDispatch();
  const userDispatch = useUserDispatch();
  const {events = [], registrationId} = useUserState();
  const videoWatched =
    events.includes(COMPLETED_SCREENING_EVENT) || (typeof window !== 'undefined' && window.location.hash === '#impact');
  const [anchorScrolled, setAnchorScrolled] = useState(false);
  //const delayedReveal = videoWatched || phase === 'replay' || phase === 'after';
  const delayedReveal = true;
  const timerEnd = new Date(webinarSchedule.endOfSalesDay * 1000 - 86400000).getTime();

  const productId = 'tntg';
  const productSku = 'tntg';
  const productName = 'TNTG Impact Kit';
  const productSlug = 'tntg-impactkit-special-wlc-30daytrial-st';
  const productValue = 67;
  const productDvdName = 'TNTG Film';
  const productDvdSlug = 'tntg-special';
  const productDvdValue = 7;

  useEffect(() => {
    registrationId &&
      axios
        .get(`${SF_API_URL}/site/${SITE_ID}/state/`, {
          params: {
            registrationId
          }
        })
        .then(({data}) => {
          if (!data.webinar) {
            data.webinar = {
              phase: 'after',
              time: Math.floor(DateTime.utc().minus({days: 5}).toSeconds())
            };
          }
          const {firedEvents = []} = data.webinar;

          phaseDispatch({
            type: 'SET_PHASE',
            payload: data
          });
          firedEvents.map((e) => userDispatch({type: 'ADD_EVENT', payload: e}));
        });
  }, []);

  useEffect(() => {
    if (anchorScrolled) return;
    if (events.includes(COMPLETED_SCREENING_EVENT)) {
      setAnchorScrolled(true);
      if (window !== undefined && window.location.hash === '#impact') {
        document.getElementById('impact-cta').scrollIntoView();
      }
    }
  }, [events, anchorScrolled]);

  useEffect(
    () =>
      track('Product Viewed', {
        product_id: productId,
        sku: productSku,
        name: productName,
        variant: productSlug,
        value: Number(productValue).toFixed(2),
        currency: 'usd'
      }),
    []
  );
  useEffect(
    () =>
      track('Product Viewed', {
        product_id: productId,
        sku: productSku,
        name: productDvdName,
        variant: productDvdSlug,
        value: Number(productDvdValue).toFixed(2),
        currency: 'usd'
      }),
    []
  );

  function CtaBox({trees = false, className = undefined}) {
    return (
      <div className={classNames(`cta-sales cta-sales-${className}`)}>
        <Container>
          <Row className="d-flex justify-content-center">
            <Col xs="12">
              <div className="offer-box offer-box-shadow px-3 py-4 px-sm-4 px-md-5 py-md-5 text-center background-white border-dashed border-green border-5 rounded">
                <div className="offer-box-inner">
                  <Container fluid>
                    <Row className="d-flex justify-content-center">
                      <Col className="text-center">
                        <div className="offer-box-heading">
                          <h3>By taking action NOW, you’ll receive over 65% off the full price of $197</h3>
                        </div>
                        <Row className="d-flex justify-content-center mb-2">
                          <Col xs="12" lg="8">
                            <img
                              className="img-fluid"
                              src="https://cdn.foodrevolution.org/tntg/tntg-productimage-digitalall.png"
                            />
                          </Col>
                        </Row>
                        <h3 className="offer-box-regular-price text-400 mb-2">
                          Regular Price: <del>$197</del>
                        </h3>
                        <h2 className="offer-box-offer-text text-condensed text-800 text-green mx-auto mb-3">
                          Special Price Only On This Page: $67
                        </h2>
                        <CheckoutButton
                          btnstyle="offer-box-button"
                          sku={productSku}
                          price={productValue}
                          slug={productSlug}
                        >
                          <FontAwesomeIcon
                            className="mr-2"
                            //@ts-ignore
                            icon={faCartPlus}
                          />{' '}
                          Buy Now
                        </CheckoutButton>
                      </Col>
                    </Row>
                    <h4 className="text-condensed text-800 mt-4">
                      PLUS Get A Treasured Health Collection ($97 Value!) Completely FREE
                    </h4>
                    <SecurityDisclaimer />
                  </Container>
                </div>
              </div>
            </Col>
          </Row>
          {trees && <TreesRow />}
        </Container>
      </div>
    );
  }

  useBalanceText();
  return (
    <div className="screening-page page d-flex flex-column">
      <PageMeta />

      <div className="page-content">
        <div className="background-gradient-yellow-white">
          {(() => {
            switch (phase) {
              case 'before':
                return (
                  <>
                    <div className="background-light-blue triangle-bottom triangle-bottom-light-blue wrapper">
                      <div className="offset-header">
                        <Container>
                          <Row>
                            <Col>
                              <h3 className="text-center text-white mb-0">
                                Thank you for saving your spot for the online screening of <em>The Need To GROW.</em>
                              </h3>
                            </Col>
                          </Row>
                        </Container>
                      </div>
                    </div>
                    <div className="video-box offset-video-box">
                      <Container>
                        <Row>
                          <Col className="px-0 px-sm-3">
                            <div className="video-box-prelaunch text-center background-black px-4 py-5 px-lg-5 text-white">
                              <Row className="d-flex justify-content-center">
                                <Col xs="12" md="10" lg="12">
                                  <h4 className="text-condensed text-800">
                                    Please look for an email from us with more information about the online screening.
                                  </h4>
                                </Col>
                              </Row>
                              <Row className="d-flex justify-content-center mb-4">
                                <Col xs="12">
                                  <h5 className="text-400 mb-0">
                                    With all grassroots movements, success depends on your support. Please use the share
                                    buttons on this page to tell your friends and family about this upcoming exclusive
                                    free online screening event! We look forward to sharing this special film with you!
                                  </h5>
                                </Col>
                              </Row>
                              <Row className="text-center">
                                <Col>
                                  <div className="mb-3">
                                    <ShareButtons alignment="justify-content-center" shareText />
                                  </div>
                                  <FollowButtons alignment="justify-content-center" followText />
                                </Col>
                              </Row>
                            </div>
                          </Col>
                        </Row>
                      </Container>
                    </div>
                  </>
                );
              case 'during':
              case 'replay':
                return (
                  <>
                    <div className="background-black triangle-bottom triangle-bottom-black wrapper">
                      <div className="offset-header-during">
                        <Container>
                          <Row>
                            <Col>
                              <h3 className="text-center text-800 text-white mb-2">
                                Welcome to the exclusive online screening of <em>The Need To GROW.</em>
                              </h3>
                              <h5 className="text-center text-800 text-white mb-0">
                                An environmental film to give the world hope.
                              </h5>
                            </Col>
                          </Row>
                        </Container>
                      </div>
                    </div>
                    <div className="video-box offset-video-box-during pb-5">
                      <Container>
                        <Row>
                          <Col className="px-0 px-sm-3">
                            <Webinar noun="Free Screening" phaseOverride={phase} />
                          </Col>
                        </Row>
                      </Container>
                    </div>
                    <Container>
                      <div className={classNames(`section-countdown`, `text-center`)}>
                        <Row className="align-items-center justify-content-center m-0">
                          <Col xs="12" lg="5" xl="4" className={`p-0`}>
                            <h3 className={`timer-heading text-condensed text-800`}>
                              The free viewing period will end in:
                            </h3>
                          </Col>
                          <Col xs="12" lg="6" xl="5" className={classNames(`timer`, `countdown-dark`)}>
                            <CountdownTimer time={timerEnd} />
                          </Col>
                        </Row>
                      </div>
                    </Container>
                  </>
                );
              case 'after':
                return (
                  <>
                    <div className="background-light-blue triangle-bottom triangle-bottom-light-blue wrapper">
                      <div className="offset-header">
                        <Container>
                          <Row>
                            <Col>
                              <h3 className="text-center text-white mb-0">
                                Your limited time free viewing period of <em>The Need To GROW</em> has ended.
                              </h3>
                            </Col>
                          </Row>
                        </Container>
                      </div>
                    </div>
                    <div className="video-box offset-video-box">
                      <Container>
                        <Row>
                          <Col className="px-0 px-sm-3">
                            <div className="video-box-prelaunch text-center background-black px-4 py-5 px-lg-5 text-white">
                              <Row className="d-flex justify-content-center mb-4">
                                <Col xs="12" lg="10" xl="8">
                                  <h5 className="text-400 mb-0">
                                    With all grassroots movements, success depends on your support. Please use the share
                                    buttons on this page to tell your friends and family about this exclusive free
                                    online screening event!
                                  </h5>
                                </Col>
                              </Row>
                              <Row className="text-center">
                                <Col>
                                  <div className="mb-3">
                                    <ShareButtons alignment="justify-content-center" shareText />
                                  </div>
                                  <FollowButtons alignment="justify-content-center" followText />
                                </Col>
                              </Row>
                            </div>
                          </Col>
                        </Row>
                      </Container>
                    </div>
                  </>
                );
            }
          })()}
        </div>

        <Divider className="mt-5" />

        <div className={classNames(`takehome wrapper`, delayedReveal ? 'triangle-angles-offset-bottom' : 'pb-5')}>
          <Container>
            <Row className="d-flex align-items-center justify-content-center">
              <Col xs="8" sm="6" lg="4" className="text-center mb-4 mb-lg-0">
                <img src="https://cdn.foodrevolution.org/tntg/social/tntg-iv-square.jpg" />
              </Col>
              <Col xs="12" md="10" lg="8" xl="7" className="text-center text-lg-left">
                <h4 className="text-condensed text-800 mb-3">
                  Feeling inspired and want to <em>stay</em> that way?
                </h4>
                <h4 className="text-condensed text-800">
                  Gain unlimited access to the film and help spread this urgent mission now.
                </h4>
                <h5 className="mb-2">
                  <strong>Right now just ${productDvdValue}!</strong>
                </h5>
                <p className="mb-4">(Less than the cost of a movie ticket!)</p>
                <CheckoutButton
                  className="btn-size-350"
                  sku={productSku}
                  price={productDvdValue}
                  slug={productDvdSlug}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <SecurityDisclaimer className="text-center mt-4" />
              </Col>
            </Row>
          </Container>
        </div>

        {delayedReveal && (
          <div className="impact-delay" id="impact-delay">
            <SalesCopy screeningTestimonials salesCta={<CtaBox />} />
          </div>
        )}
      </div>

      <div className="mt-auto">
        <Footer />
        <ShareButtons floating />
      </div>
      <ModalExitScreening />
      <RegistrationModal />
    </div>
  );
}
